import type { ProcedureQuote, Coverage } from "Care/features/quote/quote.types";

import type { CalculatedCosts, RemainingCoverage } from "./costs.types";

export const calculateProcedureCosts = (
  originalRate: number,
  {
    plan_term_key,
    include_deductible,
    coinsurance_before_deductible,
    coinsurance_after_deductible,
    copay_before_deductible,
    copay_after_deductible,
    oop_max: _oop_max,
  }: Coverage,
  { remaining_deductible, remaining_oop_max }: RemainingCoverage,
): CalculatedCosts => {
  remaining_deductible = Math.max(0, Number(remaining_deductible));
  remaining_oop_max = Math.max(0, Number(remaining_oop_max));

  let consumerCostBeforeOOPMax = 0;
  let deductibleCost = 0;
  let coinsuranceCost = 0;
  let copayCost = 0;
  let effectiveRate = originalRate;

  // Before deductible is met
  copayCost = copay_before_deductible ?? 0;
  if (copayCost > 0) {
    // If there's a copay before deductible, apply it directly to the consumer cost
    consumerCostBeforeOOPMax += copayCost;
    effectiveRate -= copayCost;
  } else if (coinsurance_before_deductible) {
    // Apply coinsurance to the original rate if no copay
    coinsuranceCost = originalRate * coinsurance_before_deductible;
    consumerCostBeforeOOPMax += coinsuranceCost;
    effectiveRate -= coinsuranceCost;
  }

  // Apply deductible if applicable and not covered by copay/coinsurance
  if (include_deductible && remaining_deductible > 0) {
    deductibleCost = Math.min(effectiveRate, remaining_deductible);
    consumerCostBeforeOOPMax += deductibleCost;
    effectiveRate -= deductibleCost;
  }

  // After deductible
  if (copay_after_deductible) {
    // If there's a copay after deductible, apply it directly to the consumer cost
    copayCost = copay_after_deductible ?? 0;
    copayCost = Math.min(copayCost, effectiveRate);
    consumerCostBeforeOOPMax += copayCost;
    if (copayCost >= effectiveRate) {
      effectiveRate = 0;
    } else {
      effectiveRate -= copayCost;
    }
  }

  if (coinsurance_after_deductible) {
    // Apply coinsurance to the effectiveRate
    coinsuranceCost = effectiveRate * coinsurance_after_deductible;
    consumerCostBeforeOOPMax += coinsuranceCost;
  }

  // Ensure consumer cost does not exceed remaining out-of-pocket max
  const consumerPays = Math.min(consumerCostBeforeOOPMax, remaining_oop_max);
  const insurancePays = originalRate - consumerPays;

  // Reduce coinsurance amount if it exceeds the remaining out-of-pocket max
  if (consumerCostBeforeOOPMax > remaining_oop_max) {
    coinsuranceCost = Math.min(coinsuranceCost, remaining_oop_max);
  }

  // Update deductible and out-of-pocket max status
  const hasMetDeductible = remaining_deductible - deductibleCost <= 0;
  const hasMetOOPMax = remaining_oop_max - consumerPays <= 0;
  const willMeetOOPMax = consumerPays >= remaining_oop_max;
  const hasCoinsurance = coinsurance_after_deductible > 0 || coinsurance_before_deductible > 0;

  const disableFields = {
    rate:
      // Flat copay (before or after deductible),
      (!hasCoinsurance && copayCost > 0) ||
      // Patient has previously met their out-of-pocket maximum (sans this expensive)
      (hasMetOOPMax && !willMeetOOPMax),
    // consurance is more nuanced and has many edge cases, best to leave the field enabled in those cases
  };

  return {
    planTermKey: plan_term_key,
    consumerPays,
    insurancePays,
    copayCost,
    coinsuranceCost,
    hasMetDeductible,
    hasMetOOPMax,
    willMeetOOPMax,
    deductibleCost,
    disableFields,
    originalRate,
  };
};

export const calculateMultipleProcedureCosts = (
  quote: ProcedureQuote,
  coverages: Coverage[],
  { remaining_deductible, remaining_oop_max }: RemainingCoverage,
): CalculatedCosts[] => {
  const billClassQuotes = [];
  Object.keys(quote.billing_class_rates).forEach((key) => {
    // Last quote in the array is the highest rate
    const billClassQuote = quote.billing_class_rates[key][quote.billing_class_rates[key].length - 1];
    billClassQuotes.push(billClassQuote);
  });

  const allCosts = coverages.map((coverage) => {
    let billClassQuote = billClassQuotes.find((billClassQuote) => billClassQuote.plan_term_key === coverage.plan_term_key);

    if (!billClassQuote) {
      // billClassQuote should never `null` when coverage.billing_class_group is "all"
      billClassQuote = {
        rate: quote[`average_rate_${coverage.billing_class_group}`],
        // // TODO: Might need to pass billClassQuote.usingAverageRate to render a tool tip stating
        // // it is an average, similar to hou UHC does it
        // usingAverageRate: true,
      };
    }
    const costs = calculateProcedureCosts(billClassQuote.rate, coverage, {
      remaining_deductible,
      remaining_oop_max,
    });

    remaining_deductible = Math.max(0, remaining_deductible - costs.deductibleCost);
    remaining_oop_max = Math.max(0, remaining_oop_max - costs.consumerPays);

    costs.billingClass = coverage.billing_class_group;
    return costs;
  });

  return allCosts;
};

export const calculateTotalCost = (allCosts: CalculatedCosts[]): CalculatedCosts => {
  const totalCost = allCosts.reduce(
    (acc, curr) => ({
      consumerPays: acc.consumerPays + curr.consumerPays,
      insurancePays: acc.insurancePays + curr.insurancePays,
      copayCost: acc.copayCost + curr.copayCost,
      coinsuranceCost: acc.coinsuranceCost + curr.coinsuranceCost,
      deductibleCost: acc.deductibleCost + curr.deductibleCost,
      hasMetDeductible: acc.hasMetDeductible || curr.hasMetDeductible,
      hasMetOOPMax: acc.hasMetOOPMax || curr.hasMetOOPMax,
      willMeetOOPMax: acc.willMeetOOPMax || curr.willMeetOOPMax,
      originalRate: acc.originalRate + curr.originalRate,
    }),
    {
      consumerPays: 0,
      insurancePays: 0,
      copayCost: 0,
      coinsuranceCost: 0,
      deductibleCost: 0,
      hasMetDeductible: false,
      hasMetOOPMax: false,
      willMeetOOPMax: false,
      originalRate: 0,
    },
  );

  return totalCost;
};
